//@flow

import {
  createTriggerEndpoint,
  deleteTriggerEndpoint,
  editTriggerEndpoint,
  endpoints,
  getTriggersEndpoint,
  getTriggerEndpoint,
  getTriggerExecutionsEndpoint,
  simulateTriggerExecutionEndpoint,
} from '../api';

import {buildRoute} from '../../../../http';

import type {
  CreateTriggerRequest,
  UpdateTriggerRequest,
  ListTriggersParams,
  ListTriggerExecutionsParams,
} from '../api';
import type {
  ChatTrigger,
  Pagination,
  PaginationResponse,
  TriggerExecution,
  TriggerExecutionSimulateResponse,
} from '../../../../entities';
import {normalizeTriggerFromApi} from '../utils/adapters';

export async function createTriggerService(
  payload: CreateTriggerRequest
): Promise<ChatTrigger> {
  const response = await createTriggerEndpoint(payload).toPromise();
  return normalizeTriggerFromApi(response);
}

export async function editTriggerService(
  triggerId: string,
  payload: UpdateTriggerRequest
): Promise<ChatTrigger> {
  const route = buildRoute(endpoints.EDIT_TRIGGER, [triggerId]);

  const response = await editTriggerEndpoint(payload, {
    route,
  }).toPromise();

  return normalizeTriggerFromApi(response);
}

export const defaultTriggerPagination = {
  page: 0,
  size: 10,
};

export async function getTriggersService(
  params?: ListTriggersParams = {}
): Promise<PaginationResponse<ChatTrigger>> {
  const {
    name,
    trigger_type,
    funnel_id,
    tag_id,
    page = defaultTriggerPagination.page,
    size = defaultTriggerPagination.size,
  } = params;

  const queryParams: any = {
    page,
    size,
  };

  if (name) {
    queryParams.name = name;
  }

  if (trigger_type) {
    queryParams.trigger_type = trigger_type;
  }

  if (funnel_id) {
    queryParams.funnel_id = funnel_id;
  }

  if (tag_id) {
    queryParams.tag_id = tag_id;
  }

  const response = await getTriggersEndpoint(queryParams).toPromise();

  return {
    ...response,
    data: (response.data || []).map(normalizeTriggerFromApi),
  };
}

export async function deleteTriggerService(triggerId: string): Promise<void> {
  const route = buildRoute(endpoints.DELETE_TRIGGER, [triggerId]);

  return deleteTriggerEndpoint({
    route,
  }).toPromise();
}

export async function getTriggerService(id: string): Promise<ChatTrigger> {
  const route = buildRoute(endpoints.GET_TRIGGER, [id]);

  const response = await getTriggerEndpoint(
    {},
    {
      route,
    }
  ).toPromise();

  return normalizeTriggerFromApi(response);
}

export const defaultTriggerExecutionPagination = {
  page: 0,
  size: 16,
};

export async function getTriggerExecutionsService(
  triggerId: string,
  params?: ListTriggerExecutionsParams = {}
): Promise<PaginationResponse<TriggerExecution>> {
  const {
    page = defaultTriggerExecutionPagination.page,
    size = defaultTriggerExecutionPagination.size,
  } = params;

  const route = buildRoute(endpoints.GET_TRIGGER_EXECUTIONS, [triggerId]);

  const response = await getTriggerExecutionsEndpoint(
    {page, size},
    {route}
  ).toPromise();

  const pagination: Pagination = response.pagination || {
    page: response.page ?? page,
    size: response.size ?? size,
    total: response.total ?? (response.data || []).length,
  };

  return {
    ...response,
    data: response.data || [],
    pagination,
  };
}

export async function simulateTriggerExecutionService(
  triggerId: string
): Promise<TriggerExecutionSimulateResponse> {
  const route = buildRoute(endpoints.SIMULATE_TRIGGER_EXECUTION, [triggerId]);

  const response = await simulateTriggerExecutionEndpoint(
    {},
    {route}
  ).toPromise();

  return {
    estimatedConversationsCount: response?.estimatedConversationsCount ?? 0,
  };
}
